<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useUserStore } from '../../stores/userStore'
import api from '../../api'
import type { BandSubscriptionDto } from '../../dtos'

const props = defineProps<{
  bandId: number
}>()

const userStore = useUserStore()
const sub = ref<BandSubscriptionDto | null>(null)
const loading = ref(false)
const errorMessage = ref('')

async function fetchSubscription() {
  if (!userStore.user) return
  loading.value = true
  errorMessage.value = ''
  try {
    const token = await userStore.user.getIdToken()
    const res = await api.get(`/bands/${props.bandId}/subscription`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    sub.value = res.data
  } catch (e: any) {
    console.error('Failed to fetch subscription:', e)
    errorMessage.value = e?.response?.status === 404
      ? 'Subscription endpoint not found. Restart the backend to pick up the new route.'
      : (e?.response?.data?.error || e?.message || 'Failed to fetch subscription.')
    sub.value = null
  } finally {
    loading.value = false
  }
}

watch(() => props.bandId, fetchSubscription, { immediate: true })

const tierLabel = computed(() => {
  if (!sub.value) return ''
  if (sub.value.isTest) return 'Test'
  return sub.value.tier.charAt(0).toUpperCase() + sub.value.tier.slice(1)
})

const tierBadgeClass = computed(() => {
  if (!sub.value) return ''
  if (sub.value.isTest) return 'bg-violet-100 text-violet-700 border-violet-200'
  switch (sub.value.tier) {
    case 'pro': return 'bg-amber-100 text-amber-700 border-amber-200'
    case 'standard': return 'bg-teal-100 text-teal-700 border-teal-200'
    case 'free':
    default: return 'bg-slate-100 text-slate-600 border-slate-200'
  }
})

const isReadOnly = computed(() => sub.value?.status === 'read_only' || sub.value?.status === 'scheduled_deletion')

const formatBytes = (b: number): string => {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(2)} GB`
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(1)} MB`
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`
  return `${b} B`
}

// Test bands bypass all caps in the backend, so render the cap portion as
// just the current value without any "X / Y" — even when the tier they happen
// to be on (set by the backfill) defines a numeric cap.
const formatCap = (current: number, max: number): string => {
  if (sub.value?.isTest || max < 0) return `${current}`
  return `${current} / ${max}`
}

const formatStorageCap = (current: number, max: number): string => {
  if (sub.value?.isTest || max < 0) return formatBytes(current)
  return `${formatBytes(current)} / ${formatBytes(max)}`
}

const formatDate = (iso?: string | null): string => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString()
}
</script>

<template>
  <div class="mt-8 pt-4 rd-divider">
    <div v-if="loading && !sub" class="text-xs text-slate-400 italic">
      <i class="pi pi-spin pi-spinner mr-1"></i> Loading subscription…
    </div>

    <div v-else-if="errorMessage" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
      <i class="pi pi-exclamation-triangle mr-1"></i> {{ errorMessage }}
    </div>

    <div v-else-if="sub">
      <!-- Header: tier badge + read-only banner if applicable -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <span class="rd-label">Subscription</span>
          <span class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border" :class="tierBadgeClass">
            {{ tierLabel }}
          </span>
        </div>
        <span v-if="sub.currentPeriodEnd" class="text-[10px] text-slate-400">
          Renews {{ formatDate(sub.currentPeriodEnd) }}
        </span>
      </div>

      <!-- Read-only / deletion banner -->
      <div v-if="isReadOnly" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
        <div class="flex items-start gap-2">
          <i class="pi pi-lock mt-0.5"></i>
          <div>
            <div class="font-semibold">This band is read-only.</div>
            <div class="mt-1">Subscription expired{{ sub.readOnlySince ? ` ${formatDate(sub.readOnlySince)}` : '' }}. Resubscribe to make changes.</div>
            <div v-if="sub.deletionScheduledAt" class="mt-1">
              Scheduled for deletion on {{ formatDate(sub.deletionScheduledAt) }}.
            </div>
          </div>
        </div>
      </div>

      <!-- AI credits -->
      <div class="grid grid-cols-2 gap-3 mb-3">
        <div class="bg-slate-50 rounded-xl p-3 border border-slate-300">
          <p v-if="sub.isTest" class="text-2xl font-bold text-slate-900">Unlimited</p>
          <template v-else>
            <p class="text-2xl font-bold text-slate-900">${{ sub.availableAiCredits.toFixed(2) }}</p>
            <p v-if="sub.cycleAiCredits > 0 || sub.topUpAiCredits > 0" class="text-[10px] text-slate-400 mt-1">
              ${{ sub.cycleAiCredits.toFixed(2) }} cycle · ${{ sub.topUpAiCredits.toFixed(2) }} top-up
            </p>
          </template>
          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">AI Credits Available</p>
        </div>
        <div class="bg-slate-50 rounded-xl p-3 border border-slate-300">
          <p class="text-2xl font-bold text-slate-900">{{ formatCap(sub.currentMembers, sub.maxMembers) }}</p>
          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Members</p>
        </div>
        <div class="bg-slate-50 rounded-xl p-3 border border-slate-300">
          <p class="text-2xl font-bold text-slate-900">{{ formatCap(sub.currentSongs, sub.maxSongs) }}</p>
          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Songs</p>
        </div>
        <div class="bg-slate-50 rounded-xl p-3 border border-slate-300">
          <p class="text-lg font-bold text-slate-900">{{ formatStorageCap(sub.currentStorageBytes, sub.maxStorageBytes) }}</p>
          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Storage</p>
        </div>
      </div>
    </div>
  </div>
</template>
